import { useEffect, useMemo, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import OmsService from "src/_cores/_services/oms.service";
import { OrderStatisticsResponse } from "src/_pages/admin/interfaces/oms-interface";
import { mapOrderStatisticToHeader } from "src/_shares/_helpers/order.helper";
import OrdersStatisticalView from "./OrdersStatisticalView";
import { StatCardProps } from "../star-card";
import {
  Autocomplete,
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import UserService from "src/_cores/_services/user.service";
import { IUserGetParam } from "src/_pages/admin/interfaces/user-interface";

import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export type OrderStatRangePreset = "currentMonth" | "quarter" | "year" | "custom";

function quarterRange(year: number, quarter: 1 | 2 | 3 | 4) {
  const firstMonth = (quarter - 1) * 3 + 1;
  const lastMonth = firstMonth + 2;
  const from = dayjs().year(year).month(firstMonth - 1).date(1).startOf("day");
  const to = dayjs().year(year).month(lastMonth - 1).endOf("month");
  return { from, to };
}

function yearRange(year: number) {
  const from = dayjs().year(year).month(0).date(1).startOf("day");
  const to = dayjs().year(year).month(11).date(31).endOf("day");
  return { from, to };
}

function currentMonthRange() {
  const now = dayjs();
  return { from: now.startOf("month"), to: now.endOf("month") };
}

function currentQuarter(): 1 | 2 | 3 | 4 {
  const m = dayjs().month();
  return (Math.floor(m / 3) + 1) as 1 | 2 | 3 | 4;
}

function buildRangeLabel(
  preset: OrderStatRangePreset,
  from: Dayjs | null,
  to: Dayjs | null,
  year: number,
  quarter: 1 | 2 | 3 | 4,
): string {
  if (!from || !to) return "Theo bộ lọc";
  if (preset === "currentMonth") return `Tháng ${from.format("M/YYYY")}`;
  if (preset === "quarter") return `Quý ${quarter} / ${year}`;
  if (preset === "year") return `Năm ${year}`;
  return `${from.format("DD/MM/YYYY")} – ${to.format("DD/MM/YYYY")}`;
}

const OrdersStatistical = () => {
  const [data, setData] = useState<OrderStatisticsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [filterUsers, setFilterUsers] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [labels, setLabels] = useState<string[]>([]);
  const [orders, setOrders] = useState<number[]>([]);
  const [headerData, setHeaderData] = useState<StatCardProps[]>([]);

  const nowInit = dayjs();
  const { from: initFrom, to: initTo } = currentMonthRange();

  const [rangePreset, setRangePreset] =
    useState<OrderStatRangePreset>("currentMonth");
  const [statYear, setStatYear] = useState(nowInit.year());
  const [statQuarter, setStatQuarter] = useState<1 | 2 | 3 | 4>(
    currentQuarter(),
  );

  const [params, setParams] = useState<{
    fromDate: Dayjs | null;
    toDate: Dayjs | null;
    status: string | null;
    userNames: string[];
  }>({
    fromDate: initFrom,
    toDate: initTo,
    status: null,
    userNames: [],
  });

  const rangeLabel = useMemo(
    () =>
      buildRangeLabel(
        rangePreset,
        params.fromDate,
        params.toDate,
        statYear,
        statQuarter,
      ),
    [rangePreset, params.fromDate, params.toDate, statYear, statQuarter],
  );

  useEffect(() => {
    getUsers();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const from = params.fromDate
        ? dayjs(params.fromDate).format("YYYY-MM-DD")
        : null;
      const to = params.toDate
        ? dayjs(params.toDate).format("YYYY-MM-DD")
        : null;
      const label = buildRangeLabel(
        rangePreset,
        params.fromDate,
        params.toDate,
        statYear,
        statQuarter,
      );
      try {
        const res = (await OmsService.getOrderStatistic({
          fromDate: from,
          toDate: to,
          status: null,
          userNames: params.userNames,
        })) as any;
        if (cancelled) return;
        const payload = res?.data;
        if (payload) {
          setData(payload);
          const lbls = payload.byMonth?.map((x: any) => x?.label ?? "") ?? [];
          const ords = payload.byMonth?.map((x: any) => x?.orderCount ?? 0) ?? [];
          setLabels(lbls);
          setOrders(ords);
          setHeaderData(mapOrderStatisticToHeader(payload, label));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    params.fromDate,
    params.toDate,
    params.userNames,
    rangePreset,
    statYear,
    statQuarter,
  ]);

  async function getUsers() {
    const p = {
      pageIndex: 1,
      pageSize: 1000,
    } as IUserGetParam;
    const res = (await UserService.getUserList(p)) as any;
    if (res?.isSucceed) {
      setUsers(res.data.items);
    }
  }

  const onChangeDate = (type: "fromDate" | "toDate", date: Dayjs | null) => {
    setRangePreset("custom");
    setParams((prev) => ({
      ...prev,
      [type]: date ? date.startOf("day") : null,
    }));
  };

  const onChangeFilterUsers = (items: any[]) => {
    const _userNames = items.map((_) => _.userName) as any;
    setFilterUsers(_userNames);
    setParams((prev) => ({ ...prev, userNames: _userNames || [] }));
  };

  const applyPreset = (preset: OrderStatRangePreset) => {
    setRangePreset(preset);
    if (preset === "currentMonth") {
      const { from, to } = currentMonthRange();
      setParams((prev) => ({ ...prev, fromDate: from, toDate: to }));
    } else if (preset === "quarter") {
      const { from, to } = quarterRange(statYear, statQuarter);
      setParams((prev) => ({ ...prev, fromDate: from, toDate: to }));
    } else if (preset === "year") {
      const { from, to } = yearRange(statYear);
      setParams((prev) => ({ ...prev, fromDate: from, toDate: to }));
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
        Khoảng thời gian: {rangeLabel}
      </Typography>

      <Grid container spacing={2} sx={{ marginBottom: 2 }} alignItems="center">
        <Grid item xs={12}>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={rangePreset}
            onChange={(_, value) => {
              if (value == null) return;
              applyPreset(value);
            }}
            aria-label="Chế độ thống kê"
          >
            <ToggleButton value="currentMonth">Tháng hiện tại</ToggleButton>
            <ToggleButton value="quarter">Theo quý</ToggleButton>
            <ToggleButton value="year">Tổng kết năm</ToggleButton>
            <ToggleButton value="custom">Tùy chọn ngày</ToggleButton>
          </ToggleButtonGroup>
        </Grid>

        {rangePreset === "quarter" && (
          <>
            <Grid item xs={6} sm={2}>
              <TextField
                label="Năm"
                type="number"
                size="small"
                value={statYear}
                onChange={(e) => {
                  const y = parseInt(e.target.value, 10);
                  if (!Number.isNaN(y) && y >= 2000 && y <= 2100) {
                    setStatYear(y);
                    if (rangePreset === "quarter") {
                      const { from, to } = quarterRange(y, statQuarter);
                      setParams((prev) => ({
                        ...prev,
                        fromDate: from,
                        toDate: to,
                      }));
                    }
                  }
                }}
                inputProps={{ min: 2000, max: 2100 }}
                fullWidth
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <FormControl size="small" fullWidth>
                <InputLabel id="quarter-label">Quý</InputLabel>
                <Select
                  labelId="quarter-label"
                  label="Quý"
                  value={statQuarter}
                  onChange={(e) => {
                    const q = Number(e.target.value) as 1 | 2 | 3 | 4;
                    setStatQuarter(q);
                    if (rangePreset === "quarter") {
                      const { from, to } = quarterRange(statYear, q);
                      setParams((prev) => ({
                        ...prev,
                        fromDate: from,
                        toDate: to,
                      }));
                    }
                  }}
                >
                  <MenuItem value={1}>Quý 1 (T1–T3)</MenuItem>
                  <MenuItem value={2}>Quý 2 (T4–T6)</MenuItem>
                  <MenuItem value={3}>Quý 3 (T7–T9)</MenuItem>
                  <MenuItem value={4}>Quý 4 (T10–T12)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </>
        )}

        {rangePreset === "year" && (
          <Grid item xs={12} sm={2}>
            <TextField
              label="Năm tổng kết"
              type="number"
              size="small"
              value={statYear}
              onChange={(e) => {
                const y = parseInt(e.target.value, 10);
                if (!Number.isNaN(y) && y >= 2000 && y <= 2100) {
                  setStatYear(y);
                  if (rangePreset === "year") {
                    const { from, to } = yearRange(y);
                    setParams((prev) => ({
                      ...prev,
                      fromDate: from,
                      toDate: to,
                    }));
                  }
                }
              }}
              inputProps={{ min: 2000, max: 2100 }}
              fullWidth
            />
          </Grid>
        )}

        <Grid item xs={12} sm={2}>
          <DatePicker
            label="Từ ngày"
            value={params.fromDate ? dayjs(params.fromDate) : null}
            onChange={(newValue) => onChangeDate("fromDate", newValue)}
            format="DD/MM/YYYY"
            disabled={rangePreset !== "custom"}
            slotProps={{
              textField: { size: "small", fullWidth: true },
            }}
          />
        </Grid>
        <Grid item xs={12} sm={2}>
          <DatePicker
            label="Đến ngày"
            value={params.toDate ? dayjs(params.toDate) : null}
            onChange={(newValue) => onChangeDate("toDate", newValue)}
            format="DD/MM/YYYY"
            disabled={rangePreset !== "custom"}
            slotProps={{
              textField: { size: "small", fullWidth: true },
            }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Autocomplete
            multiple
            id="checkboxes-tags-demo"
            options={users}
            disableCloseOnSelect
            getOptionLabel={(item: any) =>
              `${item.userId} - ${item.userFullName}`
            }
            value={
              users.length > 0 && filterUsers
                ? users.filter((item: any) =>
                    filterUsers.some((_: any) => _ === item.userName),
                  )
                : []
            }
            renderOption={(props, item: any, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  checked={selected}
                  size="small"
                />
                {item.userId} - {item.userFullName}
              </li>
            )}
            renderInput={(paramsA) => (
              <TextField
                {...paramsA}
                label="Người dùng"
                placeholder="Chọn người dùng"
                size="small"
              />
            )}
            onChange={(event, items: any) => onChangeFilterUsers(items)}
            size="small"
          />
        </Grid>
      </Grid>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={36} />
        </Box>
      )}

      {!loading && data && (
        <OrdersStatisticalView
          labels={labels}
          orders={orders}
          headerData={headerData}
          chartTitle={`Số đơn theo tháng — ${rangeLabel}`}
        />
      )}

      {!loading && !data && (
        <Typography color="text.secondary">Không có dữ liệu.</Typography>
      )}
    </LocalizationProvider>
  );
};

export default OrdersStatistical;
